interface ClassInfo {
  attributes: {
    UU_COURSE_TITLE: string;
    BLDG_CD: string;
    UU_CRSE_LOC: string;
    UU_INSTR_LIST: string;
    UU_CRSE_DAYS: string;
    UU_CRS_STRT_END_TM: string;
    START_DT: number;
    END_DT: number;
  };
}

interface ParsedClassData {
  title: string;
  location: string;
  description: string;
  daysOfWeek: number[];
  startTime: { hours: number; minutes: number };
  endTime: { hours: number; minutes: number };
  startDate: Date;
  endDate: Date;
}

const DAY_NUM_TO_RRULE: Record<number, string> = {
  0: 'SU',
  1: 'MO',
  2: 'TU',
  3: 'WE',
  4: 'TH',
  5: 'FR',
  6: 'SA'
};

/**
 * Parse 12-hour time format (e.g., "03:45PM") to 24-hour format
 */
const parseTime = (timeStr: string): { hours: number; minutes: number } => {
  const match = timeStr.match(/(\d+):(\d+)(AM|PM)/i);
  if (!match) {
    return { hours: 0, minutes: 0 };
  }

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3].toUpperCase();

  if (period === 'PM' && hours !== 12) {
    hours += 12;
  } else if (period === 'AM' && hours === 12) {
    hours = 0;
  }

  return { hours, minutes };
};

/**
 * Convert day abbreviations to numeric day of week (0 = Sunday through 6 = Saturday for JS Date)
 */
const decodeDaysToNumbers = (days: string | null | undefined): number[] => {
  if (!days || days === '') {
    return [];
  }

  const dayMap: { [key: string]: number } = {
    'Su': 0,
    'Mo': 1,
    'Tu': 2,
    'We': 3,
    'Th': 4,
    'Fr': 5,
    'Sa': 6
  };

  const daysSet = new Set<number>();

  // Handle day ranges (e.g., "Mo-Fr" becomes 1,2,3,4,5)
  if (days.includes('-')) {
    const [first, second] = days.split('-');
    const startNum = dayMap[first];
    const endNum = dayMap[second];

    if (startNum !== undefined && endNum !== undefined) {
      for (let i = startNum; i <= endNum; i++) {
        daysSet.add(i);
      }
    }
  } else {
    // Parse concatenated days (e.g., "MoWeFr" -> Mo, We, Fr)
    for (let i = 0; i < days.length; i += 2) {
      const dayCode = days.substring(i, i + 2);
      const dayNum = dayMap[dayCode];
      if (dayNum !== undefined) {
        daysSet.add(dayNum);
      }
    }
  }

  return Array.from(daysSet).sort();
};

/**
 * Backend dates are date-like values; keep their calendar day stable across local time zones.
 */
const parseCourseDate = (value: number): Date => {
  const raw = new Date(value);
  return new Date(raw.getUTCFullYear(), raw.getUTCMonth(), raw.getUTCDate());
};

const formatCalendarDateTime = (date: Date, hours: number, minutes: number): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hStr = String(hours).padStart(2, '0');
  const mStr = String(minutes).padStart(2, '0');
  return `${year}${month}${day}T${hStr}${mStr}00`;
};

const formatCalendarDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};

const getByDay = (daysOfWeek: number[]): string => daysOfWeek
  .map((dayNum) => DAY_NUM_TO_RRULE[dayNum] || 'SU')
  .join(',');

const getFirstOccurrenceDate = (startDate: Date, daysOfWeek: number[]): Date => {
  const firstOccurrence = new Date(startDate);

  if (daysOfWeek.length === 0) {
    return firstOccurrence;
  }

  const firstDay = Math.min(...daysOfWeek);
  const daysUntilFirst = (firstDay - firstOccurrence.getDay() + 7) % 7;
  firstOccurrence.setDate(firstOccurrence.getDate() + daysUntilFirst);

  return firstOccurrence;
};

/**
 * Parse class data from the class info object
 */
export const parseClassData = (classInfo: ClassInfo): ParsedClassData => {
  const { attributes } = classInfo;

  const courseShort = attributes.UU_COURSE_TITLE.split(' - ')[0];
  const location = attributes.UU_CRSE_LOC;
  const roomNum = location?.split(' ').pop() || 'N/A';
  const buildingCode = attributes.BLDG_CD.trim();

  // Parse time range (e.g., "03:45PM-06:40PM")
  const timeRange = attributes.UU_CRS_STRT_END_TM.split('-');
  const startTime = parseTime(timeRange[0].trim());
  const endTime = parseTime(timeRange[1].trim());

  // Parse days
  const daysOfWeek = decodeDaysToNumbers(attributes.UU_CRSE_DAYS);

  // Parse dates as course calendar dates (timezone-safe)
  const startDate = parseCourseDate(attributes.START_DT);
  const endDate = parseCourseDate(attributes.END_DT);

  return {
    title: courseShort,
    location: `${buildingCode} - ${roomNum}`,
    description: `Instructor - ${attributes.UU_INSTR_LIST}`,
    daysOfWeek,
    startTime,
    endTime,
    startDate,
    endDate
  };
};

/**
 * Generate Google Calendar URL
 */
export const generateGoogleCalendarUrl = (parsedData: ParsedClassData): string => {
  const {
    title,
    location,
    description,
    startDate,
    endDate,
    startTime,
    endTime,
    daysOfWeek
  } = parsedData;

  const firstOccurrence = getFirstOccurrenceDate(startDate, daysOfWeek);
  const startDateTime = formatCalendarDateTime(firstOccurrence, startTime.hours, startTime.minutes);
  const endDateTime = formatCalendarDateTime(firstOccurrence, endTime.hours, endTime.minutes);
  const byDay = getByDay(daysOfWeek);

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    location: location,
    details: description,
    dates: `${startDateTime}/${endDateTime}`
  });

  if (byDay) {
    params.set('recur', `RRULE:FREQ=WEEKLY;BYDAY=${byDay};UNTIL=${formatCalendarDate(endDate)}T235959Z`);
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/**
 * Generate ICS calendar file for Apple Calendar / Outlook
 */
export const generateIcsFile = (parsedData: ParsedClassData): string | null => {
  const { title, location, description, daysOfWeek, startTime, endTime, startDate, endDate } = parsedData;

  if (daysOfWeek.length === 0) {
    console.error('No days of week found');
    return null;
  }

  const byDay = getByDay(daysOfWeek);
  const firstOccurrence = getFirstOccurrenceDate(startDate, daysOfWeek);
  const startDateTime = formatCalendarDateTime(firstOccurrence, startTime.hours, startTime.minutes);
  const endDateTime = formatCalendarDateTime(firstOccurrence, endTime.hours, endTime.minutes);
  const untilDate = formatCalendarDate(endDate);

  // Build the iCalendar content
  const uid = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}@myclassmap`;
  const now = new Date();
  const nowStr = formatCalendarDateTime(now, now.getHours(), now.getMinutes());

  return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//MyClassMap//MyClassMap//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
BEGIN:VEVENT
UID:${uid}
DTSTAMP:${nowStr}Z
DTSTART:${startDateTime}
DTEND:${endDateTime}
RRULE:FREQ=WEEKLY;BYDAY=${byDay};UNTIL=${untilDate}T235959Z
SUMMARY:${title}
LOCATION:${location}
DESCRIPTION:${description}
END:VEVENT
END:VCALENDAR`;
};

/**
 * Download ICS file
 */
export const downloadIcsFile = (icsContent: string, filename: string = 'class-event.ics'): void => {
  const blob = new Blob([icsContent], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};


